import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, X, Phone, Mail, MapPin, Car, Calendar as CalIcon, Pencil, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalendarPage = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [confirmingCancel, setConfirmingCancel] = useState(null);

  // Current month view
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const startDate = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-01`;
      const lastDay = new Date(viewYear, viewMonth + 1, 0).getDate();
      const endDate = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${lastDay}`;
      const res = await axios.get(`${API}/bookings?start_date=${startDate}&end_date=${endDate}`);
      // Only CA jobs
      setJobs(res.data.filter(j => j.state === "CA"));
    } catch {
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchJobs(); }, [viewMonth, viewYear]);

  const startEdit = (job) => {
    setEditingJob(job.id);
    setEditForm({ ...job });
  };

  const cancelEdit = () => {
    setEditingJob(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    try {
      await axios.patch(`${API}/bookings/${editingJob}`, editForm);
      toast.success("Job updated");
      setEditingJob(null);
      setEditForm({});
      fetchJobs();
    } catch {
      toast.error("Failed to update job");
    }
  };

  const handleCancel = async (jobId) => {
    if (confirmingCancel !== jobId) {
      setConfirmingCancel(jobId);
      return;
    }
    try {
      await axios.patch(`${API}/bookings/${jobId}/cancel`);
      toast.success("Job cancelled");
      setConfirmingCancel(null);
      fetchJobs();
    } catch {
      toast.error("Failed to cancel job");
    }
  };

  const handleRebook = async (jobId) => {
    try {
      await axios.patch(`${API}/bookings/${jobId}/rebook`);
      toast.success("Job rebooked!");
      fetchJobs();
    } catch {
      toast.error("Failed to rebook");
    }
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  // Build calendar grid
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const monthName = new Date(viewYear, viewMonth).toLocaleString("en-US", { month: "long" });

  // Group jobs by booking (call) date day
  const jobsByDay = {};
  jobs.forEach(job => {
    const day = parseInt(job.call_date?.split("-")[2] || "0");
    if (day > 0) {
      if (!jobsByDay[day]) jobsByDay[day] = [];
      jobsByDay[day].push(job);
    }
  });

  const todayDate = now.getDate();
  const isCurrentMonth = viewYear === now.getFullYear() && viewMonth === now.getMonth();

  const dayJobs = selectedDay ? (jobsByDay[selectedDay] || []) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button onClick={() => navigate("/h2g")} variant="ghost" size="sm" className="flex items-center gap-1 px-2 sm:px-3">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div className="border-l border-gray-300 h-6 hidden sm:block" />
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <CalIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                CA Calendar
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={prevMonth} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            {monthName} {viewYear}
          </h2>
          <button onClick={nextMonth} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Day Headers */}
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
              <div key={d} className="py-2 text-center text-xs sm:text-sm font-semibold text-gray-600">
                {d}
              </div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7">
            {/* Empty cells for days before the 1st */}
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[60px] sm:min-h-[80px] border-b border-r border-gray-100 bg-gray-50/50" />
            ))}

            {/* Actual days */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const count = jobsByDay[day]?.length || 0;
              const isToday = isCurrentMonth && day === todayDate;
              const isSelected = selectedDay === day;

              return (
                <div
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  className={`min-h-[60px] sm:min-h-[80px] border-b border-r border-gray-100 p-1 sm:p-2 cursor-pointer transition-colors ${
                    isSelected ? "bg-blue-50 ring-2 ring-blue-500 ring-inset" :
                    isToday ? "bg-yellow-50" : "hover:bg-gray-50"
                  }`}
                  data-testid={`cal-day-${day}`}
                >
                  <div className={`text-xs sm:text-sm font-medium mb-0.5 ${isToday ? "text-blue-600 font-bold" : "text-gray-700"}`}>
                    {day}
                  </div>
                  {count > 0 && (
                    <div className="bg-blue-600 text-white text-[10px] sm:text-xs font-bold rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      {count}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected Day Jobs */}
        {selectedDay && (
          <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {monthName} {selectedDay}, {viewYear}
                <span className="ml-2 text-sm font-normal text-gray-500">({dayJobs.length} job{dayJobs.length !== 1 ? "s" : ""})</span>
              </h3>
              <button onClick={() => setSelectedDay(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {dayJobs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No CA jobs scheduled for this day</p>
            ) : (
              <div className="space-y-3">
                {dayJobs.map(job => (
                  <div key={job.id} className={`border rounded-lg p-3 sm:p-4 ${job.status === "cancelled" ? "border-red-200 bg-red-50/50 opacity-60" : "border-gray-200"}`}>
                    {editingJob === job.id ? (
                      /* EDIT MODE */
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="text-sm font-bold text-gray-900">Edit Job</h4>
                          <div className="flex gap-2">
                            <button onClick={saveEdit} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700">
                              <Check className="w-3.5 h-3.5" /> Save
                            </button>
                            <button onClick={cancelEdit} className="flex items-center gap-1 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-300">
                              <X className="w-3.5 h-3.5" /> Cancel
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {[
                            { key: "name", label: "First Name" },
                            { key: "last_name", label: "Last Name" },
                            { key: "phone", label: "Phone" },
                            { key: "email", label: "Email" },
                            { key: "address", label: "Address" },
                            { key: "city", label: "City" },
                            { key: "state", label: "State" },
                            { key: "zip", label: "Zip" },
                            { key: "install_date", label: "Install Date", type: "date" },
                            { key: "vehicle_year", label: "Year" },
                            { key: "vehicle_make", label: "Make" },
                            { key: "vehicle_model", label: "Model" },
                            { key: "discount", label: "Discount" },
                            { key: "phone_price", label: "Price" },
                          ].map(({ key, label, type }) => (
                            <div key={key}>
                              <label className="text-[10px] font-semibold text-gray-500 uppercase">{label}</label>
                              <input
                                type={type || "text"}
                                value={editForm[key] || ""}
                                onChange={(e) => setEditForm(prev => ({ ...prev, [key]: e.target.value }))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      /* VIEW MODE */
                      <>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-bold text-gray-900">
                              {job.name} {job.last_name}
                              {job.status === "cancelled" && (
                                <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold">CANCELLED</span>
                              )}
                            </h4>
                            <p className="text-xs text-gray-500">{job.city}, {job.state}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">{job.source}</span>
                            {job.status !== "cancelled" && (
                              <>
                                <button onClick={() => startEdit(job)} className="px-2 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-full text-xs font-medium transition-colors">
                                  <Pencil className="w-3 h-3 inline mr-0.5" />Edit
                                </button>
                                {confirmingCancel === job.id ? (
                                  <div className="flex items-center gap-1">
                                    <button onClick={() => handleCancel(job.id)} className="px-2 py-1 bg-red-600 text-white rounded-full text-xs font-medium">Yes</button>
                                    <button onClick={() => setConfirmingCancel(null)} className="px-2 py-1 text-gray-500 text-xs">No</button>
                                  </div>
                                ) : (
                                  <button onClick={() => handleCancel(job.id)} className="px-2 py-1 bg-red-50 text-red-600 hover:bg-red-100 rounded-full text-xs font-medium transition-colors">
                                    Cancel
                                  </button>
                                )}
                              </>
                            )}
                            {job.status === "cancelled" && (
                              <button onClick={() => handleRebook(job.id)} className="px-2 py-1 bg-green-50 text-green-700 hover:bg-green-100 rounded-full text-xs font-medium transition-colors">
                                Rebook
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-gray-600">
                          <div className="flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5" /> {job.phone}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5" /> <span className="truncate">{job.email}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5" /> <span className="truncate">{job.address}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Car className="w-3.5 h-3.5" /> {job.vehicle_year} {job.vehicle_make} {job.vehicle_model}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarPage;
